/**
 * `scrollSelectionIntoView` for slate-react's `<Editable>` (via Plate's
 * `<PlateContent>`) that reveals the caret only when the SELECTION changed.
 *
 * slate-react re-syncs the DOM selection — and calls `scrollSelectionIntoView`
 * — from a layout effect after EVERY render. It normally early-returns when the
 * DOM selection already matches, except while a mark placeholder is active: a
 * collapsed caret with pending `editor.marks` (Bold with nothing selected, or
 * useMarkResetOnNavigation stripping a highlight at the caret). In that state
 * every unrelated re-render — autosave tick, word count, page-boundary measure —
 * scrolls the caret back into view, dragging a reader who scrolled away.
 *
 * Real caret moves (typing, arrows, clicks, find-next, `select()` to a new
 * range) still go through slate-react's default scroll-if-needed.
 */
import { Range } from 'slate';
import { defaultScrollSelectionIntoView } from 'slate-react';

export type ScrollSelectionIntoView = typeof defaultScrollSelectionIntoView;

// Keyed by editor instance so a remount (crash recovery, history preview) starts
// fresh and two co-mounted editors never share state.
const lastRevealedFor = new WeakMap<object, Range>();

const cloneRange = (range: Range): Range => ({
    anchor: { path: [...range.anchor.path], offset: range.anchor.offset },
    focus: { path: [...range.focus.path], offset: range.focus.offset },
});

/**
 * Declare `range` already visible, so the guard leaves the viewport alone when
 * slate-react syncs that exact range next. Call it right before the
 * `tf.select(range)` it describes.
 *
 * Only for selections set from a pointer action on content that IS on screen —
 * a clicked table cell, a clicked row/column handle, whose range ENDS at the
 * cell's far edge and would otherwise be scrolled to. Keyboard moves must not
 * use it: there the caret should be followed.
 */
export function markSelectionRevealed(editor: object, range: Range): void {
    lastRevealedFor.set(editor, cloneRange(range));
}

export function createSelectionScrollGuard(scroll: ScrollSelectionIntoView = defaultScrollSelectionIntoView): ScrollSelectionIntoView {
    return (editor, domRange) => {
        const selection = (editor as { selection?: Range | null }).selection;
        if (!selection) {
            // Blur / deselect: forget what was revealed, so restoring that same
            // range later counts as a real move rather than a re-sync.
            lastRevealedFor.delete(editor);
            return;
        }
        const previous = lastRevealedFor.get(editor);
        if (previous && Range.equals(previous, selection)) return;
        // Copy: compare against what was revealed, not a reference Slate may reuse.
        lastRevealedFor.set(editor, cloneRange(selection));
        scroll(editor, domRange);
    };
}
